<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Copy, RefreshCw, Trash2 } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import type { QuotationDetail } from '$lib/types';

  const { quote, onClose }: { quote: QuotationDetail; onClose: () => void } = $props();

  const queryClient = useQueryClient();
  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['quotation', quote.id] });
  }

  let passwordInput = $state('');

  const shareUrl = $derived(quote.share_token ? `${location.origin}/#/share/${quote.share_token}` : '');

  const generateMut = createMutation(() => ({
    mutationFn: () => quotationsApi.createShareLink(quote.id),
    onSuccess: () => {
      invalidate();
      toast.success('Share link generated.');
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const revokeMut = createMutation(() => ({
    mutationFn: () => quotationsApi.revokeShareLink(quote.id),
    onSuccess: () => {
      invalidate();
      toast.success('Share link revoked.');
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const passwordMut = createMutation(() => ({
    mutationFn: (password: string) => quotationsApi.updateSharePassword(quote.id, password),
    onSuccess: () => {
      passwordInput = '';
      invalidate();
      toast.success('Share password updated.');
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function copyLink() {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => toast.success('Link copied to clipboard.'))
      .catch(() => toast.error('Could not copy the link.'));
  }
</script>

<div class="flex flex-col gap-4">
  {#if !quote.share_token}
    <p class="text-[13px] text-dark-gray">
      Generate a read-only link the client can open without an account — it shows the approved quotation, its PDF,
      and lets them leave comments.
    </p>
    <button
      type="button"
      class="btn-primary w-full justify-center"
      disabled={generateMut.isPending}
      onclick={() => generateMut.mutate()}
    >
      Generate share link
    </button>
  {:else}
    <div>
      <label class="text-[11px] font-semibold uppercase tracking-[.04em] text-dark-gray" for="pq-share-url"
        >Share link</label
      >
      <div class="mt-1 flex items-center gap-2">
        <input id="pq-share-url" class="input flex-1 font-mono text-xs" readonly value={shareUrl} />
        <button type="button" class="btn-secondary shrink-0" onclick={copyLink} aria-label="Copy link">
          <Copy class="h-4 w-4" />
        </button>
      </div>
    </div>

    <div>
      <label class="text-[11px] font-semibold uppercase tracking-[.04em] text-dark-gray" for="pq-share-password"
        >{quote.share_password_protected ? 'Change password' : 'Add a password (optional)'}</label
      >
      <div class="mt-1 flex items-center gap-2">
        <input
          id="pq-share-password"
          type="text"
          class="input flex-1"
          placeholder={quote.share_password_protected ? 'New password' : 'No password set'}
          bind:value={passwordInput}
        />
        <button
          type="button"
          class="btn-secondary shrink-0"
          disabled={passwordMut.isPending}
          onclick={() => passwordMut.mutate(passwordInput)}
        >
          Save
        </button>
      </div>
      {#if quote.share_password_protected}
        <button
          type="button"
          class="mt-1.5 text-xs text-dark-gray underline hover:text-navy"
          disabled={passwordMut.isPending}
          onclick={() => passwordMut.mutate('')}
        >
          Remove password protection
        </button>
      {/if}
    </div>

    <div class="flex gap-2.5 pt-1">
      <button
        type="button"
        class="btn-secondary flex-1 justify-center"
        disabled={generateMut.isPending}
        onclick={() => generateMut.mutate()}
      >
        <RefreshCw class="h-3.5 w-3.5" /> Regenerate
      </button>
      <button
        type="button"
        class="btn-secondary flex-1 justify-center text-red-600 hover:border-red-300"
        disabled={revokeMut.isPending}
        onclick={() => {
          revokeMut.mutate();
          onClose();
        }}
      >
        <Trash2 class="h-3.5 w-3.5" /> Revoke
      </button>
    </div>
  {/if}
</div>
